import { from, of, throwError } from 'rxjs';
import axios from 'axios';
import { catchError, first, map, switchMap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import queryString from 'query-string';
import { localStorageKey } from '../../../constants';

const AUTH_PARTNER_BASE_URL = `${ process.env.REACT_APP_MS_AUTHENTICATION_PARTNER_PUBLIC }`;
const SJM_BASE_URL = `${ process.env.REACT_APP_MS_SPONSORED_JOB_META_PUBLIC }`;
const GATEWAY_BASE_URL = `${ process.env.REACT_APP_FULL_DOMAIN_GATEWAY }`;
const config = { headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' } };
const JOBCASE_EMAIL_SIGN_IN_PROXY_URL = `${ AUTH_PARTNER_BASE_URL }jobcase/login`;
export const JOBCASE_EMAIL_SIGN_IN_URL = `${ GATEWAY_BASE_URL }/v1/auth/emailsignin`;
export const JOBCASE_REFRESH_URL = `${ GATEWAY_BASE_URL }/v2/auth/refresh`;

// QueryString takes in:
// username, password, rememberMe (bool)
export const logInPartner = (payload = {}) => {
   return from(
      axios({
         method: 'post',
         url: `${ AUTH_PARTNER_BASE_URL }login`,
         data: queryString.stringify(payload),
         config,
      }),
   )
      .pipe(
         first(),
         map(({ data = {} }) => {
            return data;
         }),
         catchError((err) => {
            console.error(err);
            // Surface the HTTP status so the login form can show a clear, status-specific message
            // (wrong credentials 401 vs. no access 403 vs. rate limit 429 vs. server error 5xx).
            // Browser-only — programmatic clients POST to the gateway endpoint directly and never
            // execute this code, so their behavior is unchanged.
            return of({ error: true, status: err?.response?.status ?? 0 });
         }),
      );
};

export const emailSignInJobcaseProxy = (email, password) => {
   return from(
      axios({
         method: 'post',
         url: JOBCASE_EMAIL_SIGN_IN_PROXY_URL,
         data: {
            email,
            password,
         },
         headers: {
            'Content-Type': 'application/json',
            'x-arrival-lock': uuidv4(),
         },
         credentials: 'include',
      }),
   )
      .pipe(
         first(),
         map((res) => {
            if (res?.data?.data) {
               const {
                  access_token: accessToken,
                  refresh_token: refreshToken,
                  upward_token: upwardToken,
                  arrival_key: arrivalKey,
               } = res.data.data;

               if (accessToken && refreshToken && upwardToken) {
                  localStorage.setItem(localStorageKey.jobcaseToken, accessToken);
                  localStorage.setItem(localStorageKey.jobcaseRefreshToken, refreshToken);
                  localStorage.setItem(localStorageKey.arrivalKey, arrivalKey);
                  return { token: upwardToken };
               }
            }

            return of({});
         }),
         catchError((err) => {
            console.error(err);
            // Surface the HTTP status so the login form can show a clear, status-specific message
            // (wrong credentials 401 vs. no access 403 vs. rate limit 429 vs. server error 5xx).
            // Browser-only — programmatic clients POST to the gateway endpoint directly and never
            // execute this code, so their behavior is unchanged.
            return of({ error: true, status: err?.response?.status ?? 0 });
         }),
      );
};

export const emailSignInJobcase = (email, password) => {
   return from(
      axios({
         method: 'post',
         url: JOBCASE_EMAIL_SIGN_IN_URL,
         data: {
            email,
            password,
            isUpward: true,
         },
         headers: {
            'Content-Type': 'application/json',
            'x-arrival-lock': uuidv4(),
         },
         withCredentials: true, // Ensure credentials are included
      }),
   )
      .pipe(
         first(),
         map((res) => {
            if (res?.data?.data) {
               const {
                  access_token: accessToken,
                  refresh_token: refreshToken,
                  upward_token: upwardToken,
                  arrival_key: arrivalKey,
               } = res.data.data;

               if (accessToken && refreshToken && upwardToken) {
                  localStorage.setItem(localStorageKey.jobcaseToken, accessToken);
                  localStorage.setItem(localStorageKey.jobcaseRefreshToken, refreshToken);
                  localStorage.setItem(localStorageKey.arrivalKey, arrivalKey);

                  return { token: upwardToken };
               }
            }

            return of({});
         }),
         catchError((err) => {
            console.error(err);
            // Surface the HTTP status so the login form can show a clear, status-specific message
            // (wrong credentials 401 vs. no access 403 vs. rate limit 429 vs. server error 5xx).
            // Browser-only — programmatic clients POST to the gateway endpoint directly and never
            // execute this code, so their behavior is unchanged.
            return of({ error: true, status: err?.response?.status ?? 0 });
         }),
      );
};

/**
 * Creates and retrieves an arrival using the provided browser IP address.
 *
 * @param {string} browserIpAddress - The IP address of the browser.
 *
 * @returns {Observable} - An observable that emits the arrival key.
 */
export const createAndGetArrival = (browserIpAddress) => {
   const accessToken = localStorage.getItem(localStorageKey.jobcaseToken);
   const refreshToken = localStorage.getItem(localStorageKey.jobcaseRefreshToken);

   const arrivalUrl = 'partner.upward.net';

   return from(
      fetch(`${ GATEWAY_BASE_URL }/v2/arrivals`, {
         method: 'POST',
         headers: {
            'Content-Type': 'application/json',
            'x-arrival-lock': uuidv4(),
            Authorization: `Bearer ${ accessToken }`,
            Refresh: refreshToken,
         },
         body: JSON.stringify({
            browser_ip_address: browserIpAddress,
            arrival_url: arrivalUrl,
         }),
      }),
   )
      .pipe(
         first(),
         switchMap((response) => {
            if (response.ok) {
               return from(response.json()).pipe(
                  map((res) => {
                     const {
                        arrival_key: arrivalKey,
                     } = res.data;

                     if (arrivalKey) {
                        localStorage.setItem(localStorageKey.arrivalKey, arrivalKey);

                        return { arrivalKey };
                     }
                     return {};
                  }),
               );
            }
            return throwError('Request failed');
         }),
         catchError((err) => {
            console.error(err);
            return throwError(err);
         }),
      );
};

/**
 * This function refreshes the Jobcase token by making a POST request to the authentication gateway.
 * It retrieves the current access, refresh, and arrival keys from localStorage and sends them in the request body.
 * If the response contains new tokens and arrival key, it updates localStorage with the new values.
 */
export const refreshJobcaseToken = async () => {
   return axios({
      method: 'post',
      url: JOBCASE_REFRESH_URL,
      data: {
         arrival_key: localStorage.getItem(localStorageKey.arrivalKey),
         access_token: localStorage.getItem(localStorageKey.jobcaseToken),
         refresh_token: localStorage.getItem(localStorageKey.jobcaseRefreshToken),
      },
      headers: {
         'Content-Type': 'application/json',
         'x-arrival-lock': uuidv4(),
      },
      withCredentials: false,
   }).then((response) => {
      if (response?.data?.data) {
         const {
            access_token: accessToken,
            refresh_token: refreshToken,
            arrival_key: arrivalKey,
         } = response.data.data;

         if (accessToken && refreshToken) {
            localStorage.setItem(localStorageKey.jobcaseToken, accessToken);
            localStorage.setItem(localStorageKey.jobcaseRefreshToken, refreshToken);
            localStorage.setItem(localStorageKey.arrivalKey, arrivalKey);
         }
      }

      return response?.data?.data || null;
   });
};

/**
 * Refreshes the user session by sending a request to the server to refresh the access token.
 *
 * @returns {Observable} An observable that emits the refreshed session token.
 */
export const refreshSession = () => {
   const arrivalKey = localStorage.getItem(localStorageKey.arrivalKey);
   const accessToken = localStorage.getItem(localStorageKey.jobcaseToken);
   const refreshToken = localStorage.getItem(localStorageKey.jobcaseRefreshToken);

   return from(
      axios({
         method: 'post',
         url: `${ GATEWAY_BASE_URL }/v2/auth/refresh`,
         data: {
            arrival_key: arrivalKey,
            access_token: accessToken,
            refresh_token: refreshToken,
         },
         headers: {
            'Content-Type': 'application/json',
            'x-arrival-lock': uuidv4(),
         },
      }),
   )
      .pipe(
         first(),
         map((res) => {
            if (res?.data?.data) {
               const {
                  access_token: resAccessToken,
                  refresh_token: resRefreshToken,
                  upward_token: upwardToken,
               } = res.data.data;

               console.log('Refreshed session token:', { resAccessToken, resRefreshToken, upwardToken });

               if (resAccessToken && resRefreshToken && upwardToken) {
                  localStorage.setItem(localStorageKey.jobcaseToken, resAccessToken);
                  localStorage.setItem(localStorageKey.jobcaseRefreshToken, resRefreshToken);

                  return { token: upwardToken };
               }
            }
            return of({});
         }),
         catchError((err) => {
            console.error(err);
            return throwError(err);
         }),
      );
};
